import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useToast } from "src/components/ToastWrapper/index.js";
import Toast from "src/components/ToastWrapper/Toast";
import { SudoButton } from "src/sudokit/components/SudoButton";
import { useBenchmarkTakeoverStore } from "src/stores/benchmarkTakeoverStore";
import PreferencePanel from "./PreferencePanel";
import RatingPanel from "./RatingPanel";
import SampleCard from "./SampleCard";
import WritingIndicator from "./WritingIndicator";
import { highlightRange, highlightRuns, rangeFromOffsets, rangeOffsetsWithinProse } from "./selectionTags";
import { BUILT_IN_TAGS, useSavedBenchmarkTags } from "./useSavedBenchmarkTags";
import { renderKeptProse } from "./keptProse";
import { MAX_CONTEXT_WORDS, countWords, selectPrecedingSnippet } from "./precedingSnippet";
import { useStickyTourStore } from "src/stores/stickyTourStore";
import { useComparisonDraftRecovery } from "./useComparisonDraftRecovery";
import { anchorAnnotation, resolveSourceAnnotation } from "./comparisonProse";
import "./benchmarkTakeover.css";

// ---------------------------------------------------------------------------
// Full-screen benchmark takeover — a faithful port of Jennifer's prototype
// (BenchmarkFlow "B Comparison"). Mounted once at the editor level
// (TiptapDocumentSB) and driven entirely by benchmarkTakeoverStore; the
// generation streams feed the store through benchmarkIntercept.js.
// ---------------------------------------------------------------------------

// Reveal the reading controls as soon as prose arrives; no minimum wait.
const LOADING_EXIT_MS = 460;
const CONTENT_REVEAL_MS = 620;
const SWITCH_MS = 740;
const SPLIT_MS = 1000;
const THANKS_MS = 3000;
const DRAWER_CLOSE_MS = 620;

const TAG_OPTIONS = BUILT_IN_TAGS;

function RecoverySample({ sample }) {
  const text = useBenchmarkTakeoverStore((state) => state.samples[sample].text);
  return (
    <article className="sample" data-card={sample} aria-labelledby={`bt-recovery-${sample}-title`}>
      <header><div className="sample-title"><h2 id={`bt-recovery-${sample}-title`}>Option {sample}</h2></div></header>
      <textarea
        id={`bt-recovery-${sample}`}
        className="prose"
        aria-label={`Recover Option ${sample}`}
        readOnly
        value={text}
        placeholder="This option did not generate any writing."
        style={{ width: "100%", height: "100%", minHeight: 0, resize: "none", border: 0, color: "inherit" }}
      />
    </article>
  );
}

// `prose` is the stable pane; the paragraph itself is re-resolved by index
// because the pane repaints its streamed HTML on pointer release, replacing
// the node that was clicked.
function clampSelectionToParagraph(prose, index) {
  const paragraph = prose?.querySelectorAll("p")[index];
  const selection = window.getSelection();
  if (!paragraph || !selection || selection.rangeCount === 0 || selection.isCollapsed) return;
  const range = selection.getRangeAt(0);
  const within = paragraph.contains(range.startContainer) && paragraph.contains(range.endContainer);
  if (within) return;
  const clamped = document.createRange();
  clamped.selectNodeContents(paragraph);
  selection.removeAllRanges();
  selection.addRange(clamped);
}

function LoadingStack() {
  return (
    <div className="loading-stack" aria-live="polite" aria-label="Writing">
      <div className="loading-page" aria-hidden="true"></div>
      <div className="loading-page current">
        <WritingIndicator />
      </div>
    </div>
  );
}

function ProgressSteps({ displayStage, allDone }) {
  const order = ["A", "B", "compare"];
  const currentIndex = allDone ? order.length : order.indexOf(displayStage);
  return (
    <div className="progress" aria-label="Reading progress">
      <div className="progress-steps">
        {order.map((step, index) => {
          const isDone = index < currentIndex;
          const isActive = index === currentIndex;
          return (
            <span
              key={step}
              className={`progress-step${isActive ? " active" : ""}${isDone ? " done" : ""}`}
              data-step={step}
              aria-label={`Step ${index + 1}`}
            >
              {isDone ? "" : String(index + 1)}
            </span>
          );
        })}
      </div>
    </div>
  );
}

export default function BenchmarkTakeover({ insertText, removeInterceptedCard, refreshCards, userId, documentId }) {
  const { recoverableSession, resumeComparison, dismissRecovery } = useComparisonDraftRecovery({ userId, documentId }) || {};
  const { setToast } = useToast();
  const { savedTags, rememberTag } = useSavedBenchmarkTags();

  const stage = useBenchmarkTakeoverStore((state) => state.stage);
  const sessionId = useBenchmarkTakeoverStore((state) => state.sessionId);
  const generationDone = useBenchmarkTakeoverStore((state) => state.generationDone);
  const ratingSubmitted = useBenchmarkTakeoverStore((state) => state.ratingSubmitted);
  const failure = useBenchmarkTakeoverStore((state) => state.failure);
  const preservingSamples = failure?.preserveSamples === true;
  const showingFailure = !!failure && !!sessionId;
  const interceptedCard = useBenchmarkTakeoverStore((state) => state.interceptedCard);
  const choice = useBenchmarkTakeoverStore((state) => state.choice);
  const sampleRevisions = useBenchmarkTakeoverStore((state) => state.sampleRevisions);
  const goToStage = useBenchmarkTakeoverStore((state) => state.goToStage);
  const addAnnotation = useBenchmarkTakeoverStore((state) => state.addAnnotation);
  const addDwell = useBenchmarkTakeoverStore((state) => state.addDwell);
  const recordFocusBlur = useBenchmarkTakeoverStore((state) => state.recordFocusBlur);
  const clearInterceptedCard = useBenchmarkTakeoverStore((state) => state.clearInterceptedCard);
  const sendKeep = useBenchmarkTakeoverStore((state) => state.sendKeep);
  const closeStore = useBenchmarkTakeoverStore((state) => state.close);
  const revealSamples = useBenchmarkTakeoverStore((state) => state.revealSamples);
  // Option A having started to arrive is enough to leave the loader — the rest
  // of the sample streams in underneath the reader.
  const sampleAStarted = useBenchmarkTakeoverStore((state) => state.samples.A.text.length > 0);

  const surface = useBenchmarkTakeoverStore((state) => state.surface);
  const precedingText = useBenchmarkTakeoverStore((state) => state.precedingText);
  const wordsA = useBenchmarkTakeoverStore((state) => countWords(state.samples.A.text));
  const wordsB = useBenchmarkTakeoverStore((state) => countWords(state.samples.B.text));

  const isOpen = stage !== "closed";

  // Write: the excerpt of manuscript shown above both options. Chosen once,
  // when both options are long enough for it not to dominate (or when the
  // generation ends), then frozen for the session so it never reflows the
  // prose under the reader. The budget is the shorter option's word count.
  const snippetRef = useRef({ sessionId: null, snippet: null });
  const precedingSnippet = useMemo(() => {
    if (surface !== "write" || !precedingText || !sessionId) return null;
    if (snippetRef.current.sessionId === sessionId) return snippetRef.current.snippet;
    if (!wordsA || !wordsB) return null;
    const budget = Math.min(wordsA, wordsB);
    if (!generationDone && budget < MAX_CONTEXT_WORDS) return null;
    const snippet = selectPrecedingSnippet(precedingText, budget);
    snippetRef.current = { sessionId, snippet };
    return snippet;
  }, [surface, precedingText, sessionId, wordsA, wordsB, generationDone]);
  // The thanks beat keeps the compare layout on screen behind the note.
  const displayStage = showingFailure || stage === "thanks" ? "compare" : stage === "loading" ? "A" : stage;

  // ------------------------------------------------------------- choreography
  const [revealPhase, setRevealPhase] = useState("idle"); // idle|loading|exiting|revealing|done
  const [isSwitching, setIsSwitching] = useState(false);
  const [isSplitting, setIsSplitting] = useState(false);
  const [isCompleteClosing, setIsCompleteClosing] = useState(false);
  const [compareCard, setCompareCard] = useState("A");
  const [helpOpen, setHelpOpen] = useState(false);
  // Guards the reveal timers so they are scheduled exactly once per session.
  const revealStartedRef = useRef(false);

  useEffect(() => {
    if (stage === "loading") {
      revealStartedRef.current = false;
      setRevealPhase("loading");
      setIsSwitching(false);
      setIsSplitting(false);
      setIsCompleteClosing(false);
      setCompareCard("A");
    } else if (stage === "closed") {
      revealStartedRef.current = false;
      setRevealPhase("idle");
      setIsCompleteClosing(false);
      setHelpOpen(false);
    }
  }, [stage]);

  // Reveal as soon as Option A has begun streaming; waiting for the whole
  // generation left the reader staring at "Writing..." for the full run. This
  // is a single boolean on purpose: with `generationDone` as its own
  // dependency, the generation finishing mid-reveal re-ran the effect, whose
  // cleanup cancelled the remaining timers while the ref blocked rescheduling
  // — leaving `is-loading`/`content-revealed` on the root for the session.
  const shouldReveal = generationDone || sampleAStarted;
  useEffect(() => {
    if (!isOpen || revealStartedRef.current) return undefined;
    if (!shouldReveal) return undefined;
    revealStartedRef.current = true;

    // Every phase is scheduled up front against one clock. Chaining the later
    // timers inside the first callback cancelled them: `setRevealPhase` re-runs
    // this effect, and its cleanup cleared the timer the callback had just
    // pushed — so the reveal stalled in "exiting" forever, leaving the loader
    // mounted and `.is-loading` stuck on the root.
    const timers = [
      setTimeout(() => {
        revealSamples();
        setRevealPhase("exiting");
      }, 0),
      setTimeout(() => setRevealPhase("revealing"), LOADING_EXIT_MS),
      setTimeout(() => setRevealPhase("done"), LOADING_EXIT_MS + CONTENT_REVEAL_MS),
    ];
    return () => timers.forEach(clearTimeout);
    // `sessionId` is a dependency so a session that begins with prose already
    // present in the same batch as the previous one closed (shouldReveal never
    // flips false in between) still schedules its reveal instead of staying
    // on the loader.
  }, [shouldReveal, isOpen, sessionId, revealSamples]);

  const moveForward = useCallback(() => {
    const current = useBenchmarkTakeoverStore.getState().stage;
    if (current === "A") {
      setIsSwitching(true);
      goToStage("B");
      setTimeout(() => setIsSwitching(false), SWITCH_MS);
    } else if (current === "B") {
      if (!useBenchmarkTakeoverStore.getState().sampleRated("A")) {
        goToStage("A");
        return;
      }
      setCompareCard("A");
      setIsSplitting(true);
      goToStage("compare");
      setTimeout(() => setIsSplitting(false), SPLIT_MS);
    }
  }, [goToStage]);

  const moveBack = useCallback(() => {
    const current = useBenchmarkTakeoverStore.getState().stage;
    if (current === "B") {
      setIsSwitching(true);
      goToStage("A");
      setTimeout(() => setIsSwitching(false), SWITCH_MS);
    } else if (current === "compare") {
      setIsSplitting(false);
      setCompareCard("A");
      goToStage("B");
    }
  }, [goToStage]);

  // ---------------------------------------------------------------- failures
  useEffect(() => {
    if (!failure || showingFailure) return;
    setToast(failure.message);
    closeStore();
  }, [failure, showingFailure, setToast, closeStore]);


  // Remove an intercepted Write placeholder card from the right bar. The
  // generation's real card is written server-side; the placeholder would only
  // duplicate it, and it must not show the samples outside the takeover.
  useEffect(() => {
    if (!interceptedCard) return;
    if (removeInterceptedCard) removeInterceptedCard(interceptedCard);
    clearInterceptedCard();
  }, [interceptedCard, removeInterceptedCard, clearInterceptedCard]);

  // Dropping the placeholder also drops the link to the server card, so the
  // generation only appeared in History after a manual reload. Re-query once
  // the takeover closes — however it closed, rated or abandoned — so the prose
  // the user paid for is reachable straight away.
  const wasOpenRef = useRef(false);
  useEffect(() => {
    if (isOpen) {
      wasOpenRef.current = true;
      return;
    }
    if (!wasOpenRef.current) return;
    wasOpenRef.current = false;
    if (refreshCards) refreshCards();
  }, [isOpen, refreshCards]);

  // ---------------------------------------------------------------- telemetry
  // readMs accumulates only while the pane's stage is active, the tab is
  // visible, and the window is focused.
  const activeSampleRevision = sampleRevisions[stage];
  useEffect(() => {
    if (stage !== "A" && stage !== "B") return undefined;
    const pane = stage;
    let startedAt = null;
    const isEngaged = () => document.visibilityState === "visible" && document.hasFocus();
    const start = () => {
      if (startedAt === null && isEngaged()) startedAt = Date.now();
    };
    const stop = () => {
      if (startedAt !== null) {
        if (useBenchmarkTakeoverStore.getState().sessionId === sessionId) addDwell(pane, Date.now() - startedAt, activeSampleRevision);
        startedAt = null;
      }
    };
    start();
    const onVisibility = () => (document.visibilityState === "visible" ? start() : stop());
    const onFocus = () => start();
    const onBlur = () => stop();
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("focus", onFocus);
    window.addEventListener("blur", onBlur);
    return () => {
      stop();
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("blur", onBlur);
    };
  }, [stage, sessionId, activeSampleRevision, addDwell]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onBlur = () => recordFocusBlur();
    window.addEventListener("blur", onBlur);
    return () => window.removeEventListener("blur", onBlur);
  }, [isOpen, recordFocusBlur]);

  // ------------------------------------------------------------------ finish
  // The rating is already saved when the thank-you beat starts, so the outcome
  // (insert the kept option, record /keep) is applied right then, once per
  // session. Deferring it behind the animation let a close, or a document
  // switch that unmounts the editor, drop the chosen prose — and since the
  // store is global, a remount re-ran the effect and inserted it again.
  const completedSessionRef = useRef(null);
  // Read through a ref so a parent re-render handing down a new `insertText`
  // identity cannot restart the close timers and keep the takeover on screen.
  const insertTextRef = useRef(insertText);
  insertTextRef.current = insertText;
  useEffect(() => {
    if (stage !== "thanks") return undefined;
    const { sessionId: thanksSession, submittedChoice, submittedKeptOutput, choice, samples } = useBenchmarkTakeoverStore.getState();
    if (completedSessionRef.current !== thanksSession) {
      completedSessionRef.current = thanksSession;
      // Insert what the rating recorded, not whatever the choice control says
      // now: the two can differ if the user flipped it while Finish was in flight.
      const finalChoice = submittedChoice || choice;
      const insert = insertTextRef.current;
      if (submittedKeptOutput !== null) {
        // A restored copy of an already completed session must not insert again.
      } else if ((finalChoice === "A" || finalChoice === "B") && insert) {
        insert(renderKeptProse(samples[finalChoice].text));
        sendKeep(finalChoice, thanksSession);
      } else {
        sendKeep("none", thanksSession);
      }
    }
    const closeTimer = setTimeout(() => setIsCompleteClosing(true), THANKS_MS);
    const finishTimer = setTimeout(() => {
      setIsCompleteClosing(false);
      closeStore();
    }, THANKS_MS + DRAWER_CLOSE_MS);
    return () => {
      clearTimeout(closeTimer);
      clearTimeout(finishTimer);
    };
  }, [stage, sendKeep, closeStore]);

  // A finished comparison must not follow the writer to the next document:
  // if the editor unmounts during the thank-you beat, end the session here so
  // a remounted takeover does not replay it.
  useEffect(
    () => () => {
      const state = useBenchmarkTakeoverStore.getState();
      if (state.stage === "thanks") state.close();
    },
    []
  );

  // ------------------------------------------------------- modal containment
  const rootRef = useRef(null);
  const panelRef = useRef(null);

  // An onboarding sticky tour waits for card-streaming and insert signals that
  // a comparison never sends, so it would stall behind the takeover. End it.
  useEffect(() => {
    if (!isOpen) return;
    const tour = useStickyTourStore.getState();
    if (tour.activeTourId) tour.endTour(true);
  }, [isOpen]);

  // The takeover is modal: while it is open the page behind it is inert so
  // Tab cannot reach the editor, and focus returns where it was on close.
  useEffect(() => {
    if (!isOpen) return undefined;
    const root = rootRef.current;
    const previouslyFocused = document.activeElement;
    const madeInert = [];
    Array.from(document.body.children).forEach((element) => {
      if (element === root || element.hasAttribute("inert")) return;
      element.setAttribute("inert", "");
      madeInert.push(element);
    });
    if (root && !root.contains(document.activeElement)) root.focus({ preventScroll: true });
    return () => {
      madeInert.forEach((element) => element.removeAttribute("inert"));
      if (previouslyFocused && typeof previouslyFocused.focus === "function" && document.contains(previouslyFocused)) {
        previouslyFocused.focus({ preventScroll: true });
      }
    };
  }, [isOpen]);

  // Each step moves focus to its panel so keyboard and screen-reader users
  // land on the new questions instead of on <body>.
  useEffect(() => {
    if (stage !== "A" && stage !== "B" && stage !== "compare") return;
    panelRef.current?.focus({ preventScroll: true });
  }, [stage]);

  // Copy protection has to cover the whole takeover, not just the prose
  // panes: Select All from the Close button (or any control) selects both
  // samples and the copy event never passes through a pane. Events that do
  // start inside a pane are already handled there.
  useEffect(() => {
    if (!isOpen || ratingSubmitted || preservingSamples) return undefined;
    const selectionTouchesProse = () => {
      const selection = window.getSelection();
      const root = rootRef.current;
      if (!selection || !root || selection.isCollapsed || selection.rangeCount === 0) return false;
      const panes = root.querySelectorAll(".prose");
      for (let i = 0; i < selection.rangeCount; i += 1) {
        const range = selection.getRangeAt(i);
        for (const pane of panes) {
          if (range.intersectsNode(pane)) return true;
        }
      }
      return false;
    };
    const guard = (event) => {
      if (event.target?.closest?.(".prose")) return;
      if (!selectionTouchesProse()) return;
      event.preventDefault();
      useBenchmarkTakeoverStore.getState().recordCopyAttempt();
    };
    document.addEventListener("copy", guard, true);
    document.addEventListener("cut", guard, true);
    document.addEventListener("dragstart", guard, true);
    return () => {
      document.removeEventListener("copy", guard, true);
      document.removeEventListener("cut", guard, true);
      document.removeEventListener("dragstart", guard, true);
    };
  }, [isOpen, ratingSubmitted, preservingSamples]);

  // ------------------------------------------------------------- close dialog
  const closeDialogRef = useRef(null);

  const openCloseDialog = () => {
    if (showingFailure && (!preservingSamples || failure.historySaved === true)) {
      closeStore();
      return;
    }
    const dialog = closeDialogRef.current;
    if (dialog && !dialog.open) dialog.showModal();
  };

  const dismissCloseDialog = () => {
    const dialog = closeDialogRef.current;
    if (dialog && dialog.open) dialog.close();
  };

  const confirmClose = () => {
    dismissCloseDialog();
    // "I'm done": no insertion, nothing extra posted — the server expires the
    // session.
    closeStore();
  };

  const copyRecoverySample = async (sample) => {
    const text = useBenchmarkTakeoverStore.getState().samples[sample].text;
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setToast(`Option ${sample} copied.`);
    } catch {
      // Browser permissions can deny clipboard access. Leave the full text
      // selected so the writer can use the browser's ordinary Copy command.
      const field = document.getElementById(`bt-recovery-${sample}`);
      field?.focus();
      field?.select();
      setToast(`Option ${sample} selected. Use your browser's Copy command to save it.`);
    }
  };

  const copyRecoveryDiagnostics = async () => {
    const state = useBenchmarkTakeoverStore.getState();
    try {
      await navigator.clipboard.writeText(`Comparison session: ${state.sessionId}\nSurface: ${state.surface}\n${state.failure?.message || ""}`);
      setToast("Comparison details copied.");
    } catch {
      const field = document.getElementById("bt-recovery-session");
      field?.focus();
      field?.select();
      setToast("Session ID selected. Use your browser's Copy command to save it.");
    }
  };

  const handleDialogClick = (event) => {
    const dialog = closeDialogRef.current;
    if (!dialog) return;
    const rect = dialog.getBoundingClientRect();
    const inside =
      event.clientX >= rect.left && event.clientX <= rect.right && event.clientY >= rect.top && event.clientY <= rect.bottom;
    if (!inside) dialog.close();
  };

  // ------------------------------------------------------- selection tagging
  const tagMenuRef = useRef(null);
  const tagTooltipRef = useRef(null);
  const pendingRangeRef = useRef(null);
  const [tagMenuOpen, setTagMenuOpen] = useState(false);
  const [tagMenuAnchor, setTagMenuAnchor] = useState(null);
  const [otherMode, setOtherMode] = useState(false);
  const [otherValue, setOtherValue] = useState("");
  const [tooltip, setTooltip] = useState(null); // { text, rect }

  const hideTagMenu = useCallback(() => {
    setTagMenuOpen(false);
    setOtherMode(false);
    setOtherValue("");
    pendingRangeRef.current = null;
  }, []);

  // A pending selection belongs to one sample of one session. Moving to
  // another step, or a failure/fallback/new generation swapping the session
  // out from under the menu, must not leave a menu that tags the new text.
  useEffect(() => {
    hideTagMenu();
    setTooltip(null);
  }, [stage, sessionId, hideTagMenu]);

  useEffect(() => {
    const pending = pendingRangeRef.current;
    if (pending && pending.sampleRevision !== sampleRevisions[pending.sample]) {
      hideTagMenu();
      setTooltip(null);
    }
  }, [sampleRevisions, hideTagMenu]);

  const activeSelectionRange = useCallback(() => {
    const currentStage = useBenchmarkTakeoverStore.getState().stage;
    if (currentStage !== "A" && currentStage !== "B") return null;
    const selection = window.getSelection();
    if (!selection || selection.isCollapsed || selection.rangeCount === 0) return null;
    const range = selection.getRangeAt(0);
    const prose =
      range.commonAncestorContainer.nodeType === Node.ELEMENT_NODE
        ? range.commonAncestorContainer.closest(".prose")
        : range.commonAncestorContainer.parentElement?.closest(".prose");
    if (!prose || prose.dataset.sample !== currentStage) return null;
    if (!prose.contains(range.startContainer) || !prose.contains(range.endContainer)) return null;
    const startElement = range.startContainer.nodeType === Node.ELEMENT_NODE ? range.startContainer : range.startContainer.parentElement;
    const endElement = range.endContainer.nodeType === Node.ELEMENT_NODE ? range.endContainer : range.endContainer.parentElement;
    if (!startElement?.closest("p") || !endElement?.closest("p")) return null;
    return range;
  }, []);

  const showTagMenuForSelection = useCallback(() => {
    const range = activeSelectionRange();
    if (!range) {
      hideTagMenu();
      return;
    }
    const rect = range.getBoundingClientRect();
    if (!rect.width && !rect.height) return;
    const offsets = rangeOffsetsWithinProse(range);
    if (!offsets) return;
    const state = useBenchmarkTakeoverStore.getState();
    pendingRangeRef.current = anchorAnnotation(state.samples[state.stage].text, {
      ...offsets,
      text: range.toString(),
      sample: state.stage,
      sessionId: state.sessionId,
      sampleRevision: state.sampleRevisions[state.stage],
    });
    setTooltip(null);
    setOtherMode(false);
    setOtherValue("");
    setTagMenuAnchor({ left: rect.left, top: rect.top, width: rect.width, height: rect.height, bottom: rect.bottom });
    setTagMenuOpen(true);
  }, [activeSelectionRange, hideTagMenu]);

  const showTagMenuForHighlight = useCallback((highlight) => {
    const state = useBenchmarkTakeoverStore.getState();
    const prose = highlight?.closest(".prose");
    const sample = prose?.dataset.sample;
    if (!highlight || !["A", "B"].includes(sample) || ![sample, "compare"].includes(state.stage) || state.submitting || state.ratingSubmitted || state.failure) return false;
    const range = document.createRange();
    range.selectNodeContents(highlight);
    const offsets = rangeOffsetsWithinProse(range);
    if (!offsets) return false;
    const annotation = state.annotations.find((candidate) => {
      if (candidate.sample !== sample || (candidate.custom || candidate.tag) !== highlight.dataset.tag) return false;
      const resolved = resolveSourceAnnotation(state.samples[sample].text, candidate);
      return resolved.start <= offsets.start && resolved.end >= offsets.end && !!rangeFromOffsets(prose, resolved);
    });
    if (!annotation) return false;
    const rect = highlight.getBoundingClientRect();
    pendingRangeRef.current = { ...annotation, annotation, sessionId: state.sessionId, sampleRevision: state.sampleRevisions[sample] };
    setTooltip(null);
    setOtherMode(false);
    setOtherValue("");
    setTagMenuAnchor({ left: rect.left, top: rect.top, width: rect.width, height: rect.height, bottom: rect.bottom });
    setTagMenuOpen(true);
    return true;
  }, []);

  const applyTag = useCallback(
    (tagLabel, customText = null) => {
      const pending = pendingRangeRef.current;
      if (!pending) return;
      const state = useBenchmarkTakeoverStore.getState();
      if (pending.sampleRevision !== state.sampleRevisions[pending.sample]) {
        hideTagMenu();
        return;
      }
      const { stage: currentStage, sessionId: currentSession } = state;
      if (pending.annotation) {
        if (currentSession === pending.sessionId && [pending.sample, "compare"].includes(currentStage) && !state.submitting && !state.ratingSubmitted) {
          const original = { ...pending.annotation };
          delete original.custom;
          state.replaceAnnotation(pending.annotation, { ...original, tag: customText ? "other" : tagLabel, ...(customText ? { custom: customText } : {}) });
          if (customText) rememberTag(customText);
        }
        hideTagMenu();
        return;
      }
      const prose = rootRef.current?.querySelector(`.prose[data-sample="${pending.sample}"]`);
      const range = currentStage === pending.sample && currentSession === pending.sessionId
        ? rangeFromOffsets(prose, resolveSourceAnnotation(state.samples[pending.sample].text, pending)) : null;
      // Text that already carries a tag is left alone, so what got wrapped can
      // be less than the selection. Record exactly the highlighted words —
      // one annotation per contiguous run — so the payload matches the reader.
      const runs = range ? highlightRuns(highlightRange(range, customText || tagLabel)) : [];
      if (runs.length === 0) {
        hideTagMenu();
        return;
      }
      window.getSelection()?.removeAllRanges();
      runs.forEach((run) => {
        addAnnotation(anchorAnnotation(useBenchmarkTakeoverStore.getState().samples[currentStage].text, {
          sample: currentStage,
          tag: customText ? "other" : tagLabel,
          text: run.text,
          start: run.start,
          end: run.end,
          ...(customText ? { custom: customText } : {}),
        }));
      });
      if (customText) rememberTag(customText);
      hideTagMenu();
    },
    [addAnnotation, hideTagMenu, rememberTag]
  );

  const removePendingTag = () => {
    const pending = pendingRangeRef.current;
    const state = useBenchmarkTakeoverStore.getState();
    if (pending?.annotation && state.sessionId === pending.sessionId && pending.sampleRevision === state.sampleRevisions[pending.sample] && [pending.sample, "compare"].includes(state.stage) && !state.submitting && !state.ratingSubmitted) {
      state.replaceAnnotation(pending.annotation, null);
    }
    hideTagMenu();
  };

  // Position popovers once rendered (they must be measurable).
  useLayoutEffect(() => {
    if (!tagMenuOpen || !tagMenuAnchor || !tagMenuRef.current) return;
    placePopover(tagMenuRef.current, tagMenuAnchor);
  }, [tagMenuOpen, tagMenuAnchor, otherMode]);

  useLayoutEffect(() => {
    if (!tooltip || !tagTooltipRef.current) return;
    placePopover(tagTooltipRef.current, tooltip.rect);
  }, [tooltip]);

  function placePopover(popover, rect) {
    const margin = 12;
    popover.style.left = "0px";
    popover.style.top = "0px";
    const popoverRect = popover.getBoundingClientRect();
    const centeredLeft = rect.left + rect.width / 2 - popoverRect.width / 2;
    const left = Math.max(margin, Math.min(window.innerWidth - popoverRect.width - margin, centeredLeft));
    const preferredTop = rect.top - popoverRect.height - 10;
    const fallbackTop = rect.bottom + 10;
    const top = preferredTop >= margin ? preferredTop : Math.min(window.innerHeight - popoverRect.height - margin, fallbackTop);
    popover.style.left = `${left}px`;
    popover.style.top = `${Math.max(margin, top)}px`;
  }

  const handleRootMouseUp = (event) => {
    const highlight = event.target.closest?.(".text-highlight");
    if (highlight && window.getSelection()?.isCollapsed && showTagMenuForHighlight(highlight)) return;
    if (event.target.closest && event.target.closest(".prose")) {
      // A triple-click on the last (or only) paragraph has no following block
      // to stop at, so the browser runs the selection out of the pane into
      // the next card. Clamp it to the paragraph that was clicked.
      const prose = event.target.closest(".prose");
      const paragraph = event.detail >= 3 ? event.target.closest("p") : null;
      const paragraphIndex = paragraph ? Array.from(prose.querySelectorAll("p")).indexOf(paragraph) : -1;
      window.setTimeout(() => {
        if (paragraphIndex >= 0) clampSelectionToParagraph(prose, paragraphIndex);
        showTagMenuForSelection();
      }, 0);
    }
  };

  // Tab wraps inside the dialog. The page behind is inert, but without a
  // wrap the first/last control still hands focus to the browser chrome.
  const handleRootKeyDown = (event) => {
    if ((event.key === "Enter" || event.key === " ") && event.target.closest?.(".text-highlight")) {
      if (showTagMenuForHighlight(event.target.closest(".text-highlight"))) event.preventDefault();
      return;
    }
    if (event.key !== "Tab" || !rootRef.current) return;
    const focusable = Array.from(
      rootRef.current.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])')
    ).filter((element) => !element.closest("[hidden]") && !element.closest("dialog:not([open])"));
    if (focusable.length === 0) return;
    const active = document.activeElement;
    const outside = !rootRef.current.contains(active) || active === rootRef.current;
    const atStart = active === focusable[0];
    const atEnd = active === focusable[focusable.length - 1];
    if (!(event.shiftKey ? atStart || outside : atEnd || outside)) return;
    // Candidates hidden by CSS refuse focus; walk from the edge until one takes it.
    const candidates = event.shiftKey ? focusable.slice().reverse() : focusable;
    for (const element of candidates) {
      element.focus();
      if (document.activeElement === element) {
        event.preventDefault();
        return;
      }
    }
  };

  // The menu is anchored to where the words were; once the pane scrolls it
  // would float over different text. The selection stays, so a mouseup or
  // Shift+Arrow reopens it in place.
  const handleRootScrollCapture = (event) => {
    if (tagMenuOpen && event.target?.closest?.(".prose")) hideTagMenu();
  };

  const handleRootKeyUp = (event) => {
    if (event.key === "Shift" || event.key.startsWith("Arrow")) {
      if (event.target.closest && event.target.closest(".prose")) {
        window.setTimeout(showTagMenuForSelection, 0);
      }
    }
    if (event.key === "Escape") {
      setHelpOpen(false);
      hideTagMenu();
      setTooltip(null);
    }
  };

  const handleRootMouseOver = (event) => {
    const highlight = event.target.closest && event.target.closest(".text-highlight");
    if (highlight) {
      setTooltip({ text: highlight.dataset.tag || "", rect: highlight.getBoundingClientRect() });
    }
  };

  const handleRootMouseOut = (event) => {
    if (event.target.closest && event.target.closest(".text-highlight")) {
      setTooltip(null);
    }
  };

  // Click-away handling for the tag menu + help popover.
  useEffect(() => {
    if (!isOpen) return undefined;
    const onMouseDown = (event) => {
      if (tagMenuRef.current && !tagMenuRef.current.contains(event.target) && !(event.target.closest && event.target.closest(".prose"))) {
        hideTagMenu();
      }
      if (helpOpen && !(event.target.closest && event.target.closest(".help-widget"))) {
        setHelpOpen(false);
      }
    };
    document.addEventListener("mousedown", onMouseDown);
    return () => document.removeEventListener("mousedown", onMouseDown);
  }, [isOpen, helpOpen, hideTagMenu]);

  if (!isOpen) {
    if (!recoverableSession) return null;
    return createPortal(
      <Toast dismissible onDismiss={dismissRecovery} dismissButtonVariant="inverted">
        <div role="status" aria-label="Unfinished comparison" className="flex max-w-[calc(100vw-8rem)] flex-wrap items-center gap-3">
          <span>Comparison ready to review.</span>
          <SudoButton variant="invertedColorSecondary" size="default" className="text-[var(--context-menu-text)]" onClick={resumeComparison}>
            Resume review
          </SudoButton>
        </div>
      </Toast>,
      document.body,
    );
  }

  const rootClasses = [
    "bt-root",
    !showingFailure && (revealPhase === "loading" || revealPhase === "exiting") ? "is-loading" : "",
    !showingFailure && revealPhase === "exiting" ? "loading-exit" : "",
    !showingFailure && (revealPhase === "exiting" || revealPhase === "revealing") ? "content-revealed" : "",
    isSwitching ? "is-switching" : "",
    isSplitting ? "is-splitting" : "",
    stage === "thanks" ? "is-thanking" : "",
    isCompleteClosing ? "is-complete-closing" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return createPortal(
    <div
      ref={rootRef}
      className={rootClasses}
      role="dialog"
      aria-modal="true"
      aria-label="Help us improve Sudowrite’s writing"
      tabIndex={-1}
      data-stage={displayStage}
      data-compare-card={compareCard}
      data-saved-choice={choice || ""}
      onMouseUp={handleRootMouseUp}
      onPaste={(event) => { if (event.target?.matches?.("textarea, input")) useBenchmarkTakeoverStore.getState().recordFeedbackPaste(); }}
      onKeyDown={handleRootKeyDown}
      onKeyUp={handleRootKeyUp}
      onScrollCapture={handleRootScrollCapture}
      onMouseOver={handleRootMouseOver}
      onMouseOut={handleRootMouseOut}
    >
      <div className="takeover">
        <header className="topbar">
          <div className="brand" aria-label="Comparison session">
            <div>
              <h1>Help us improve Sudowrite’s writing</h1>
            </div>
          </div>
          <div className="topbar-actions">
            {!showingFailure && <ProgressSteps
              displayStage={displayStage}
              allDone={stage === "thanks"}
            />}
            <button
              className="close-button"
              type="button"
              aria-label="Close comparison"
              onClick={openCloseDialog}
            ></button>
          </div>
        </header>

        <div className="bt-main">
          <section
            className="workspace"
            aria-label="Full screen comparison"
            style={showingFailure && !preservingSamples ? { gridTemplateColumns: "1fr" } : undefined}
          >
            {(!showingFailure || preservingSamples) && <div className="reader-grid">
              <LoadingStack />
              <button
                className="compare-nav prev"
                type="button"
                aria-label="Show previous sample"
                onClick={() => setCompareCard((card) => (card === "A" ? "B" : "A"))}
              >
                ‹
              </button>
              <button
                className="compare-nav next"
                type="button"
                aria-label="Show next sample"
                onClick={() => setCompareCard((card) => (card === "A" ? "B" : "A"))}
              >
                ›
              </button>
              {preservingSamples ? <RecoverySample sample="A" /> : <SampleCard key={`A:${sampleRevisions.A}`} sample="A" precedingSnippet={precedingSnippet} />}
              {preservingSamples ? <RecoverySample sample="B" /> : <SampleCard key={`B:${sampleRevisions.B}`} sample="B" precedingSnippet={precedingSnippet} />}
            </div>}

            <aside
              ref={panelRef}
              className="panel"
              aria-label={showingFailure ? "Comparison recovery" : "Rating panel"}
              tabIndex={-1}
              style={showingFailure && !preservingSamples ? { maxWidth: "40rem", width: "100%", margin: "2rem auto" } : undefined}
            >
              {showingFailure ? (
                <div>
                  <h2>{preservingSamples ? "Save your writing" : "Comparison could not finish"}</h2>
                  <p role="alert">{failure.message}</p>
                  {failure.refund?.status === "pending" && <p>Credit refund: pending. Any charged credits are queued for return.</p>}
                  {failure.refund?.status === "granted" && failure.refund.amount > 0 && <p>Credit refund: {failure.refund.amount.toLocaleString()} credits returned.</p>}
                  {preservingSamples && failure.historySaved === true && <p>The available writing is saved in History. Your manuscript has not been changed.</p>}
                  <p>{recoverableSession?.sessionId === sessionId ? "Your comparison is ready to review. Resume to finish your feedback." : preservingSamples ? "This comparison cannot be rated. Copy any available writing you want to use." : "You can copy these details when reporting this issue."}</p>
                  <label htmlFor="bt-recovery-session">Comparison session</label>
                  <input id="bt-recovery-session" readOnly value={sessionId || ""} style={{ width: "100%" }} />
                  <button type="button" onClick={copyRecoveryDiagnostics}>Copy diagnostic details</button>
                  <div className="dialog-actions">
                    {recoverableSession?.sessionId === sessionId && <button type="button" onClick={resumeComparison}>Resume review</button>}
                    {preservingSamples && <button type="button" disabled={!wordsA} onClick={() => copyRecoverySample("A")}>Copy A</button>}
                    {preservingSamples && <button type="button" disabled={!wordsB} onClick={() => copyRecoverySample("B")}>Copy B</button>}
                    <button className="done-button" type="button" onClick={openCloseDialog}>Close</button>
                  </div>
                </div>
              ) : displayStage === "compare" ? (
                <PreferencePanel
                  onBack={moveBack}
                  onFinished={() => goToStage("thanks")}
                  busy={stage === "thanks"}
                />
              ) : (
                <RatingPanel
                  key={`${displayStage}:${sampleRevisions[displayStage]}`}
                  sample={displayStage}
                  onNext={moveForward}
                  onBack={moveBack}
                />
              )}
            </aside>
          </section>
        </div>
      </div>

      <div className="help-widget">
        <button
          className="help-button"
          type="button"
          aria-label="Why am I seeing this?"
          aria-expanded={helpOpen}
          onClick={() => setHelpOpen((open) => !open)}
        ></button>
        <section
          className="help-card"
          hidden={!helpOpen}
          aria-labelledby="bt-help-title"
        >
          <h2 id="bt-help-title">Why am I seeing this?</h2>
          <p>You’re using Muse 2 Alpha to compare and give feedback on generated prose. Choose a different Prose Mode in Write or Draft to stop comparing prose.</p>
        </section>
      </div>

      <div
        className="tag-menu"
        role="menu"
        ref={tagMenuRef}
        hidden={!tagMenuOpen}
      >
        {otherMode ? (
          <form
            onSubmit={(event) => {
              event.preventDefault();
              const value = otherValue.trim();
              if (value) applyTag("other", value);
            }}
          >
            <input
              type="text"
              autoFocus
              placeholder="add your own tag…"
              maxLength={60}
              value={otherValue}
              onChange={(event) => setOtherValue(event.target.value)}
            />
          </form>
        ) : (
          <>
            {TAG_OPTIONS.map((tag) => (
              <button
                key={tag}
                type="button"
                onClick={() => applyTag(tag)}
              >
                {tag}
              </button>
            ))}
            {savedTags.filter((tag) => !TAG_OPTIONS.some((option) => option.toLowerCase() === tag.toLowerCase())).map((tag) => (
              <button key={`custom:${tag}`} type="button" onClick={() => applyTag("other", tag)}>
                {tag}
              </button>
            ))}
            <button
              type="button"
              onClick={() => setOtherMode(true)}
            >
              other…
            </button>
            {pendingRangeRef.current?.annotation && <button type="button" onClick={removePendingTag}>Remove tag</button>}
          </>
        )}
      </div>
      <div
        className="tag-tooltip"
        ref={tagTooltipRef}
        hidden={!tooltip}
      >
        {tooltip?.text || ""}
      </div>

      <dialog
        className="close-dialog"
        ref={closeDialogRef}
        aria-labelledby="bt-close-dialog-title"
        onClick={handleDialogClick}
      >
        <h2 id="bt-close-dialog-title">Leave this comparison?</h2>
        <p>{preservingSamples
          ? "We could not confirm that these options were saved to History. Make sure you have copied any writing you want to keep before closing."
          : "Your rating won’t be saved. Both options stay in History, but the generation credits are only returned when you finish."}</p>
        <div className="dialog-actions">
          <button
            type="button"
            onClick={dismissCloseDialog}
          >
            {preservingSamples ? "Keep copying" : "Keep comparing"}
          </button>
          <button
            className="done-button"
            type="button"
            onClick={confirmClose}
          >
            {preservingSamples ? "Close without saving" : "Leave without rating"}
          </button>
        </div>
      </dialog>
    </div>,
    document.body
  );
}
